import React from 'react'
import { Card, CardContent, Box, Typography, Chip, Skeleton } from '@mui/material'
import { Doughnut } from 'react-chartjs-2'
import { SMBDashboardSummary } from '../../../services/api'
import { SOURCE_COLOR_MAP } from '../constants'
import { LEAD_SOURCE_LABELS, LeadSource } from '../../../types/lead'

interface LeadSourcesChartProps {
  summary: SMBDashboardSummary | null;
  summaryLoading: boolean;
}

export const LeadSourcesChart: React.FC<LeadSourcesChartProps> = ({ summary, summaryLoading }) => {
  const sources = summary?.lead_sources ?? []
  
  const sourceChartData = {
    labels: sources.map((s) => LEAD_SOURCE_LABELS[s.source as LeadSource] || s.source),
    datasets: [
      {
        data: sources.map((s) => s.count),
        backgroundColor: sources.map((s) => SOURCE_COLOR_MAP[s.source] ?? '#CBD5E1'),
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  }

  return (
    <Card sx={{ height: '100%', borderRadius: '16px', border: '1px solid', borderColor: '#F1F5F9', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
      <CardContent sx={{ p: '24px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ letterSpacing: '-0.5px', color: '#0F172A' }}>
            Lead Sources
          </Typography>
          <Chip label="Last 30 Days" size="small" sx={{ bgcolor: '#F8FAFC', color: '#64748B', fontWeight: 600, fontSize: '0.75rem', border: '1px solid #F1F5F9' }} />
        </Box>
        <Typography variant="body2" color="#64748B" sx={{ mb: 3 }}>
          Top acquisition channels
        </Typography>
        
        {summaryLoading && !summary ? (
          <Skeleton variant="rectangular" height={260} />
        ) : sources.length === 0 ? (
          <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">No lead source data yet.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 2 }}>
            <Box sx={{ position: 'relative', height: 200, width: 200, flexShrink: 0, mx: 'auto' }}>
              <Doughnut
                data={sourceChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  devicePixelRatio: window.devicePixelRatio > 1 ? window.devicePixelRatio : 2,
                  cutout: '80%',
                  layout: { padding: 0 },
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: '#1E293B',
                      padding: 12,
                      titleFont: { size: 14, weight: 600, family: 'Inter, sans-serif' },
                      bodyFont: { size: 14, weight: 500, family: 'Inter, sans-serif' },
                      cornerRadius: 8,
                      displayColors: true,
                      boxPadding: 4,
                    }
                  },
                  elements: {
                    arc: {
                      borderWidth: 0,
                      borderRadius: 4,
                    }
                  }
                }}
              />
              {/* Center Text (Total Leads) */}
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#0F172A', lineHeight: 1 }}>
                  {(summary?.total_leads ?? 0) >= 1000 ? ((summary?.total_leads ?? 0) / 1000).toFixed(1) + 'k' : (summary?.total_leads ?? 0)}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#64748B', mt: 0.5 }}>
                  Leads
                </Typography>
              </Box>
            </Box>
            
            {/* Custom HTML Legend */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, width: '100%' }}>
              {sources.map((s) => (
                <Box key={s.source} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SOURCE_COLOR_MAP[s.source] ?? '#CBD5E1', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>
                      {LEAD_SOURCE_LABELS[s.source as LeadSource] || s.source}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0F172A' }}>
                    {s.count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
